import { InfoCircleTwoTone, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Form,
  Button,
  Row,
  Col,
  message,
  Typography,
  Modal,
  Space,
  Card,
  Tooltip,
} from 'antd';
import Clipboard, { Event } from 'clipboard';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import IconCopy from '@/assets/images/public/icon-copy.react.svg?react';
import { BusiCode } from '@/infrastructure/axios/code';
import { BASE_NAME, pushHistory } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { getProductId } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { useReportPage } from '@/infrastructure/reporter/datong/report';
import { Platform } from '@/infrastructure/utils/app';
import { formatTimestamp } from '@/infrastructure/utils/timestamp';
import { DEFAULT_TIME_RANGE_ISSUES as DEFAULT_TIME_RANGE } from '@/presentation/components/form/components/time-range';
import { getRoleByUserForPro } from '@/presentation/components/user-manage/api';
import { IProductDetailInfo, OrgType } from '@/proto/manage';
import { Dimension, ResourceStatus } from '@/proto/resource-manager';
import { productService } from '@/service/product';
import { resourcePackageService } from '@/service/resource-package';
import style from './style.module.scss';

const { Text } = Typography;

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};


enum AppPlatformMap {
  '未知' = -1,
  'Android' = 1,
  'iOS' = 2,
  'Mac' = 4,
  'Flutter' = 9,
  'Windows' = 10,
  'Linux' = 5,
  'Harmony' = Platform.HarmonyOS
}

enum AppTypeMap {
  '-' = 0,
  '办公商务' = 3,
  '聊天通讯' = 4,
  '系统输入' = 5,
  '手机美化' = 6,
  '阅读学习' = 7,
  '出行地图' = 8,
  '影音视听' = 9,
  '儿童亲子' = 10,
  '拍摄美化' = 11,
  '购物时尚' = 12,
  '生活娱乐' = 13,
  '实用工具' = 14,
  '动作冒险' = 15,
  '飞行射击' = 16,
  '角色扮演' = 17,
  '经营策略' = 18,
  '体育竞速' = 19,
  '网络游戏' = 20,
  '益智游戏' = 21,
  '休闲娱乐' = 22,
}

export enum OrgTypeName {
  '未知'=OrgType.UNKNOWN_ORG,
  '公司'=OrgType.COMPANY,
  '组织'=OrgType.ORGANIZATION,
  '个人'=OrgType.INDIVIDUAL
}

export interface UpdateAppInfoReq {
  appId: string;
  desc: string;
  epcBizId: string;
  epcBizName: string;
  epcSubBizId: string;
  epcSubBizName: string;
  logoUrl: string;
  name: string;
  pid: number;
  pkgSign: string;
  type: number;
  userId: string;
}

export interface DeleteAppReq {
  appId: string;
  pid: string;
  userId: string;
}


const ProductInfoPro = () => {
  useReportPage(PAGE_ID.setting.productInfo);
  const [clipboard, setClipboard] = useState<any>(null);
  const [appInfo, setAppInfo] = useState<IProductDetailInfo>();
  const [resourceCount, setResourceCount] = useState<number>(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [form] = Form.useForm();
  const history = useHistory();

  const load = async () => {
    const data = await productService.getProductInfo({ productId: getProductId() });
    if (Array.isArray(data)) return;

    setAppInfo(data);
    const isAdmin = await getRoleByUserForPro();
    setIsAdmin(isAdmin);
    // 获取有效资源包数量
    const resourcePackageData = await resourcePackageService.getProductResourceList({
      queryParam: {
        pageParam: {
          page: 1,
          pageSize: 10,
        }, // 有效资源包和分页情况的参数无关
        timeParam: {
          startTime: moment(DEFAULT_TIME_RANGE[0]).startOf('date')
            .unix(),
          endTime: moment(DEFAULT_TIME_RANGE[1]).endOf('date')
            .unix(),
        }, // 获取有效资源包对日期参数的要求是日期需要包含今天在内，与后台协商用此默认时间
      },
      resourceStatus: ResourceStatus.VALID,
      dim: Dimension.PRODUCT,
    });
    setResourceCount(resourcePackageData?.total);
  };

  useEffect(() => {
    load();
    setClipboard(new Clipboard('#copyBtn'));
    return () => clipboard?.destroy();
  }, []);

  useEffect(() => {
    clipboard?.on('success', () => {
      message.destroy();
      message.success('复制成功');
    });
    clipboard?.on('error', (e: Event) => {
      message.destroy();
      message.error('复制失败');
      logger.error('clipboard', e?.trigger, e?.action);
    });
  }, [clipboard]);


  const onDelete = async () => {
    Modal.confirm({
      title: `删除产品${appInfo?.productName}`,
      icon: <InfoCircleTwoTone />,
      content: (
        <span>
          已经清楚了删除产品产生的影响，并确定要删除产品？
          {resourceCount > 0 && `（该产品绑定了${resourceCount}个资源包。在删除产品前，需要先完成用量抵扣和资源包解绑，整个流程会比较长，请耐心等待。）`}
        </span>
      ),
      onOk: async () => {
        const res = await productService.deleteProduct({});
        if (res.baseRsp?.code === BusiCode.Ok) {
          message.success('删除产品成功');
          location.href = `${BASE_NAME}/workbench/apps`;
        } else if (res.baseRsp?.code === BusiCode.ExceedCantCancel) {
          message.success('由于该产品绑定的资源包不足，导致用量抵扣失败，暂无法删除该产品。');
        }
      },
    });
  };

  const renderPlatform = () => {
    if (!!appInfo?.isFlutter) {
      return AppPlatformMap[Platform.Flutter];
    }
    return AppPlatformMap[appInfo?.platformId || -1] || '其他';
  };

  return <Card bordered={false}>
    <div className={style.productInfoContainer}>
      <p className={style.title}>产品信息 <Tooltip title={'产品信息暂不支持修改，如果您需要修改产品信息，请联系我们的客户服务团队以获取更多帮助。感谢您的理解和合作！'}
        overlayStyle={{ maxWidth: 500 }}>
        <QuestionCircleOutlined />
      </Tooltip></p>
      <Form
        {...layout}
        style={{ width: '100%' }}
        form={form}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="产品名称">
              <span>{appInfo?.productName}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="产品类型" >
              <span>{AppTypeMap[appInfo?.type || 0]}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="APP ID" >
              <span id="appId">{appInfo?.productId}</span>
              <IconCopy
                id="copyBtn"
                className={style.copyIcon}
                data-clipboard-target="#appId" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="APP KEY" >
              <span id="appKey">{appInfo?.appKey}</span>
              <IconCopy
                id="copyBtn"
                className={style.copyIcon}
                data-clipboard-target="#appKey" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="组织类型" >
              <span>{OrgTypeName[appInfo?.orgType || 0]}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="创建人" >
              <span>{appInfo?.creator ?? '-'}</span>
            </Form.Item>
          </Col>
          {appInfo?.orgType === OrgType.COMPANY || appInfo?.orgType === OrgType.ORGANIZATION ? <Col span={12}>
            <Form.Item label={`${OrgTypeName[appInfo?.orgType]}名称`} >
              <span>{appInfo?.company ?? '-'}</span>
            </Form.Item>
          </Col> : null}
          <Col span={12}>
            <Form.Item label="平台" >
              <span>{renderPlatform()}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="创建时间" >
              <span>{appInfo?.createTime ? formatTimestamp(appInfo?.createTime, 'YYYY-MM-DD HH:mm:ss') : '-'}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="资源包" >
              <Button type='link' style={{ padding: 0 }} onClick={() => pushHistory(history, '/setting/product/resource')}>用量明细</Button>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
        </Row>
        <Form.Item
          labelCol={{ span: 2 }}
          wrapperCol={{ span: 22 }}
          name="description"
          label="产品描述"
          className={style.formItem}
          key={appInfo?.description}
        >
          <span>{appInfo?.description ?? '-'}</span>
        </Form.Item>
        <div style={{ textAlign: 'right' }}>
          <Space size={22}>
            <Text type="warning">
              点击删除产品后，该产品的所有数据将被永久移除，无法再查看或下载。请注意，此操作不可撤销。
            </Text>
            <Button style={{ background: '#EDF1F7', border: 'none' }} onClick={onDelete} disabled={!isAdmin}>删除产品</Button>
          </Space>
        </div>
      </Form>
    </div>
  </Card>;
};

export default ProductInfoPro;
